// Google reCAPTCHA ENTERPRISE (invisible) — bot protection for login/
// registration/OTP. The site key comes from VITE_RECAPTCHA_SITE_KEY
// (frontend/.env.local); the backend verifies tokens via the Enterprise
// Assessment API (see app/services/recaptcha.py). When the key is absent this
// module is a no-op: getRecaptchaToken resolves to undefined and the backend
// skips verification, so local dev works without any keys.

const SITE_KEY: string = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "";

let loaderPromise: Promise<void> | null = null;

function loadRecaptchaScript(): Promise<void> {
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise<void>((resolve, reject) => {
    if ((window as any).grecaptcha?.enterprise) { resolve(); return; }
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${SITE_KEY}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loaderPromise = null; // allow a retry on the next call
      reject(new Error("Failed to load reCAPTCHA"));
    };
    document.head.appendChild(script);
  });
  return loaderPromise;
}

/**
 * Warm up reCAPTCHA on page load so the v3 badge appears immediately (it
 * otherwise only loads on the first token fetch, i.e. on submit — which makes
 * it look like reCAPTCHA "isn't showing"). No-op when no site key is set.
 * Safe to call on mount of any auth page.
 */
export function preloadRecaptcha(): void {
  if (!SITE_KEY) return;
  loadRecaptchaScript().catch(() => {
    /* badge just won't preload — getRecaptchaToken still retries on submit */
  });
}

/**
 * Fetch a reCAPTCHA v3 token for the given action ("login", "register", …).
 * Never throws — returns undefined when the key is missing or Google is
 * unreachable, and the backend fails open in the same situations.
 */
export async function getRecaptchaToken(action: string): Promise<string | undefined> {
  if (!SITE_KEY) return undefined;
  try {
    await loadRecaptchaScript();
    const enterprise = (window as any).grecaptcha?.enterprise;
    if (!enterprise) return undefined;
    await new Promise<void>((resolve) => enterprise.ready(resolve));
    return await enterprise.execute(SITE_KEY, { action });
  } catch {
    return undefined;
  }
}
